import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { ThemeProvider, createTheme } from '@mui/material';

import ColorWrapper from '@styles/colors';
import { font as initialFont } from '@styles/fonts';
import { addQueryParams, removeQueryParam, firstLetterUp, isWeb } from '@helpers';
import { scale as initialScale } from '@components/frame';
import { modalFieldBg } from '@v2/pages/common/constants/base.constants';
import ClearFilters from '@components/search/components/ClearFilters';
import NoResult from '@components/search/components/noResult';
import Button from '@components/button';
import { transformToOData, getPersistedTableConfig, clearPersistedFilters, clearPersistedSort } from './helpers';

export { getPersistedTableConfig };

const PrimaryKeyField = ({ value, C }) => {
  return (
    <View
      style={{
        width: '100%',
        height: '100%',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <Text numberOfLines={1} style={{ color: C('Label/Light/Primary') }}>
        {value}
      </Text>
    </View>
  );
};

const DataGrid = (props) => {
  const {
    C,
    columns,
    data,
    primaryKey,
    apiRef,
    customAnchorRef,
    sortModel,
    search,
    density,
    columnVisibilityModel,
    setColumnVisibilityModel,
    filterModel,
    setFilterModel,
    loaded,
    isError,
    setOrderBy,
    setSortModel,
    setODataFilter,
    idName,
    onRowsScrollEnd,
    onRowClick,
    fetchData,
    tableName,
    getDetailPanelHeight,
    getDetailPanelContent,
    bg,
    isPopupMode,
    clearSearchAndFilters,
    searchValue,
    scale = initialScale,
    font = initialFont,
  } = props;

  // --- Internal state for persistence (used when external props not provided) ---
  const initialPersistedConfig = useRef(getPersistedTableConfig(tableName)).current;
  const [internalSortModel, setInternalSortModel] = useState(initialPersistedConfig?.sortModel || []);
  const [internalFilterModel, setInternalFilterModel] = useState(initialPersistedConfig?.filterModel || null);
  const [internalColumnVisibilityModel, setInternalColumnVisibilityModel] = useState(initialPersistedConfig?.columnVisibilityModel || {});

  // Resolve: prefer external props, fall back to internal state
  // Use !== undefined so explicit null from parent is respected (not overridden by internal state)
  const resolvedSortModel = sortModel !== undefined ? sortModel : internalSortModel;
  const resolvedFilterModel = filterModel !== undefined ? filterModel : internalFilterModel;
  const resolvedColumnVisibilityModel = columnVisibilityModel !== undefined ? columnVisibilityModel : internalColumnVisibilityModel;

  const [selectionModel, setSelectionModel] = useState([]);
  const internalAnchorRef = useRef(null);
  const abortControllerRef = useRef(null);
  const previousODataFilterRef = useRef(initialPersistedConfig?.oDataFilter || null);
  const isFilterPanelOpenRef = useRef(false);
  const pendingODataFilterRef = useRef(null);
  const pendingFilterModelRef = useRef(null);

  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: 'Roboto',
        },
      }),
    []
  );

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      pendingODataFilterRef.current = null;
      pendingFilterModelRef.current = null;
      previousODataFilterRef.current = null;
    };
  }, []);

  // Execute filter search - called on Enter key or panel close
  const executeFilterSearch = useCallback(() => {
    const oDataFilter = pendingODataFilterRef.current;
    const filters = pendingFilterModelRef.current;

    // Only make API call if filter actually changed
    if (oDataFilter !== previousODataFilterRef.current) {
      previousODataFilterRef.current = oDataFilter;

      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // Update query params
      if (oDataFilter) {
        addQueryParams({
          filterModel: JSON.stringify(filters),
          oDataFilter,
        });
      }

      if (search) {
        search({
          oDataFilter,
          filterModel: filters,
          signal: abortControllerRef.current.signal,
        });
      }
    }
  }, [search]);

  // Listen for Enter key when filter panel is open (web only)
  useEffect(() => {
    if (!isWeb) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Enter' && isFilterPanelOpenRef.current) {
        executeFilterSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [executeFilterSearch]);

  // Subscribe to filter panel open/close events
  useEffect(() => {
    if (!apiRef?.current) return;

    const unsubscribeOpen = apiRef.current.subscribeEvent('preferencePanelOpen', (params) => {
      if (params.openedPanelValue === 'filters') {
        isFilterPanelOpenRef.current = true;
      }
    });

    const unsubscribeClose = apiRef.current.subscribeEvent('preferencePanelClose', () => {
      if (isFilterPanelOpenRef.current) {
        isFilterPanelOpenRef.current = false;
        // Execute search when panel closes
        executeFilterSearch();
      }
    });

    return () => {
      unsubscribeOpen();
      unsubscribeClose();
    };
  }, [apiRef, executeFilterSearch]);

  const handleSortChange = async (column) => {
    if (!column.length) {
      clearPersistedSort(tableName);
      if (setSortModel) setSortModel([]);
      else setInternalSortModel([]);
      if (setOrderBy) setOrderBy([]);
      if (search) {
        await search({ orderBy: [] });
        removeQueryParam('orderBy');
        removeQueryParam('sortModel');
      }
      return;
    }

    if (tableName) {
      try {
        window.localStorage.setItem(`${tableName}-sortModel`, JSON.stringify(column));
      } catch {}
    }

    const orderBy = column.map((sort) => `${firstLetterUp(sort.field)} ${sort.sort}`);
    if (setOrderBy) setOrderBy(orderBy);
    if (setSortModel) setSortModel(column);
    else setInternalSortModel(column);

    if (search) {
      await search({ orderBy });
      addQueryParams({ orderBy: JSON.stringify(orderBy), sortModel: JSON.stringify(column) });
    }
  };

  const handleFilterModelChange = (filters) => {
    const filterItems = filters?.items ?? [];

    if (setFilterModel) setFilterModel(filters?.items?.length ? filters : null);
    else setInternalFilterModel(filters?.items?.length ? filters : null);

    // Handle reset case
    if (!filterItems.length) {
      if (setODataFilter) setODataFilter(null);
      pendingODataFilterRef.current = null;
      pendingFilterModelRef.current = null;

      clearPersistedFilters(tableName);

      // Only trigger reset search if we had a filter before
      if (previousODataFilterRef.current !== null) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        removeQueryParam('filterModel');
        removeQueryParam('oDataFilter');

        previousODataFilterRef.current = null;

        // Immediate search on reset
        if (search) search({ oDataFilter: null });
      }
      return;
    }

    const oDataFilterArr = filterItems.map(transformToOData).filter(Boolean);

    if (!oDataFilterArr.length) {
      if (setODataFilter) setODataFilter(null);
      pendingODataFilterRef.current = null;
      pendingFilterModelRef.current = null;
      return;
    }

    const logic = filters.logicOperator ?? 'and';
    const oDataFilter = oDataFilterArr.length > 1 ? oDataFilterArr.join(` ${logic} `) : oDataFilterArr[0];

    // Persist to localStorage (after oDataFilter is computed)
    if (tableName) {
      try {
        window.localStorage.setItem(`${tableName}-filterModel`, JSON.stringify(filters));
        window.localStorage.setItem(`${tableName}-oDataFilter`, oDataFilter);
      } catch {}
    }

    // Sync state update - immediate (for UI display)
    if (setODataFilter) setODataFilter(oDataFilter);

    // Store pending filter for Enter key or panel close
    pendingODataFilterRef.current = oDataFilter;
    pendingFilterModelRef.current = { ...filters, items: filterItems };
  };

  const handleColumnVisibilityModelChange = (newModel) => {
    // Persist to localStorage
    if (tableName) {
      try {
        window.localStorage.setItem(`${tableName}-columnVisibility`, JSON.stringify(newModel));
      } catch {}
    }

    if (setColumnVisibilityModel) setColumnVisibilityModel(newModel);
    else setInternalColumnVisibilityModel(newModel);

    if (Object.keys(newModel).length) {
      addQueryParams({ columnVisibilityModel: JSON.stringify(newModel) });
    } else {
      removeQueryParam('columnVisibilityModel');
    }
  };

  const rows = data.filter((row) => typeof row !== 'string');

  const [reorderedColumns, setColumns] = useState(() => {
    const savedOrder = window.localStorage.getItem(`${tableName}-order`);
    const savedWidth = window.localStorage.getItem(`${tableName}-columnWidths`);

    if (!savedOrder && !savedWidth) {
      return columns;
    }

    let initial = columns;
    const columnsMapInit = new Map(columns.map((col) => [col.field, col]));

    if (savedOrder) {
      const parsedOrder = JSON.parse(savedOrder);
      initial = parsedOrder.map((field) => columnsMapInit.get(field)).filter(Boolean);
    }

    if (savedWidth) {
      const parsedWidth = JSON.parse(savedWidth);
      initial = initial.map((col) => {
        const savedW = parsedWidth[col.field];
        if (savedW !== undefined) {
          const { flex, ...rest } = col;
          return { ...rest, width: savedW };
        }
        return col;
      });
    }

    return initial;
  });
  const columnsMap = useMemo(() => new Map(columns.map((col) => [col.field, col])), [columns]);

  useEffect(() => {
    const savedOrder = window.localStorage.getItem(`${tableName}-order`);
    const savedWidth = window.localStorage.getItem(`${tableName}-columnWidths`);

    if (!savedOrder && !savedWidth) {
      setColumns(columns);
      return;
    }

    let initial = columns;

    if (savedOrder) {
      const parsedOrder = JSON.parse(savedOrder);
      initial = parsedOrder.map((field) => columnsMap.get(field)).filter(Boolean);
    }

    if (savedWidth) {
      const parsedWidth = JSON.parse(savedWidth);
      initial = initial.map((col) => {
        const savedW = parsedWidth[col.field];
        if (savedW !== undefined) {
          const { flex, ...rest } = col;
          return { ...rest, width: savedW };
        }
        return col;
      });
    }

    setColumns(initial);
  }, [columns, tableName]);

  const onColumnOrderChange = ({ oldIndex, targetIndex }) => {
    const updatedColumns = Array.from(reorderedColumns);
    const [movedColumn] = updatedColumns.splice(oldIndex, 1);
    updatedColumns.splice(targetIndex, 0, movedColumn);

    setColumns(updatedColumns);
    const newOrder = updatedColumns.map((col) => col.field);
    window.localStorage.setItem(`${tableName}-order`, JSON.stringify(newOrder));
  };

  const onColumnWidthChange = (params) => {
    const key = `${tableName}-columnWidths`;
    const storage = window.localStorage.getItem(key);
    const savedWidths = storage ? JSON.parse(storage) : {};
    savedWidths[params.colDef.field] = params.width;
    window.localStorage.setItem(key, JSON.stringify(savedWidths));
  };

  const reorderedColumnsWithLink = useMemo(() => {
    return reorderedColumns.map((column) => {
      const enriched = {
        ...column,
        sortable: column.sortable === true,
        filterable: column.filterable === true,
      };

      if (primaryKey && column.field === primaryKey) {
        enriched.renderCell = (params) => (
          <View style={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', paddingVertical: scale(5) }}>
            <PrimaryKeyField {...{ value: params.value, C }} />
          </View>
        );
      }

      return enriched;
    });
  }, [reorderedColumns, primaryKey, C.scheme]);

  useEffect(() => {
    let timeoutId = null;

    const selectedRows = apiRef?.current?.getSelectedRows();
    if (selectedRows?.size) {
      timeoutId = setTimeout(() => {
        apiRef?.current?.setRowSelectionModel([]);
      }, 2000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [apiRef]);

  const clearFiltersRef = useRef(null);
  clearFiltersRef.current = () => {
    if (clearSearchAndFilters) {
      clearSearchAndFilters();
      return;
    }
    handleFilterModelChange({ items: [], logicOperator: resolvedFilterModel?.logicOperator });
  };

  const slots = useMemo(
    () => ({
      noRowsOverlay: () => {
        if (isError) {
          return <ClearFilters onPress={() => clearFiltersRef.current?.()} />;
        }
        return (
          <NoResult
            C={C}
            value={searchValue}
            style={{ marginTop: 0, width: '100%', height: '100%' }}
            button={
              <Button
                variant="Dialog L"
                type="Primary"
                name={i18n.t('Clear filters')}
                bg={C('Button/Light/Primary')}
                width={scale(140)}
                height={scale(53)}
                borderRadius={scale(12)}
                onPress={() => clearFiltersRef.current?.()}
              />
            }
          />
        );
      },
    }),
    [isError, C, searchValue, scale]
  );

  const getAnchorEl = useCallback(
    () => customAnchorRef?.current ?? internalAnchorRef.current ?? apiRef?.current?.rootElementRef?.current ?? null,
    [customAnchorRef, apiRef]
  );

  const slotProps = useMemo(
    () => ({
      panel: {
        anchorEl: getAnchorEl,
        placement: 'bottom-end',
        disablePortal: false,
        popperOptions: { strategy: 'fixed' },
        style: isPopupMode ? { zIndex: Number.MAX_SAFE_INTEGER } : undefined,
      },
      columnMenu: {
        disablePortal: false,
      },
      basePopper: {
        style: isPopupMode ? { zIndex: Number.MAX_SAFE_INTEGER } : undefined,
      },
      baseMenu: {
        style: isPopupMode ? { zIndex: Number.MAX_SAFE_INTEGER } : undefined,
        slotProps: {
          paper: { style: isPopupMode ? { zIndex: Number.MAX_SAFE_INTEGER } : undefined },
        },
      },
    }),
    [getAnchorEl, isPopupMode]
  );

  const emptyInitialState = useMemo(() => ({}), []);

  const getRowId = useCallback((row) => `${row?.createdOn}_${row?.[idName]}`, [idName]);

  return (
    <View style={{ width: '100%', flex: 1 }}>
      <div ref={internalAnchorRef} style={{ position: 'absolute', top: 0, right: 0, width: 1, height: 1, pointerEvents: 'none' }} />
      <ThemeProvider theme={theme}>
        <DataGridPro
          filterMode="server"
          sortingMode="server"
          slots={slots}
          // selectionModel={selectionModel}
          onColumnOrderChange={onColumnOrderChange}
          onColumnWidthChange={onColumnWidthChange}
          onRowClick={onRowClick ? (el) => onRowClick(el.row) : null}
          onRowsScrollEnd={onRowsScrollEnd}
          loading={!loaded}
          filterModel={resolvedFilterModel?.items ? resolvedFilterModel : { items: [] }}
          onFilterModelChange={handleFilterModelChange}
          density={density}
          columnVisibilityModel={resolvedColumnVisibilityModel || {}}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          onSortModelChange={handleSortChange}
          sortModel={resolvedSortModel || []}
          initialState={emptyInitialState}
          slotProps={slotProps}
          apiRef={apiRef}
          columns={reorderedColumnsWithLink}
          rows={isError ? [] : rows}
          getRowId={getRowId}
          getDetailPanelHeight={getDetailPanelHeight}
          getDetailPanelContent={getDetailPanelContent}
          sx={{
            border: 'none',
            '.MuiDataGrid-cell': {
              color: C('Label/Light/Secondary'),
              fontSize: scale(14),
            },
            '.MuiDataGrid-columnHeaders': {
              minWidth: '100%',
            },
            '.MuiDataGrid-row--borderBottom > div:nth-child(2)': {
              paddingLeft: '18px',
            },
            '.MuiDataGrid-columnHeader': {
              color: C('Label/Light/Secondary'),
              backgroundColor: bg ?? C('Background / Light / System'),
              fontSize: scale(14),
            },
            '.MuiDataGrid-row': {
              '--rowBorderColor': C('Hairline/Light/No trans'),
            },
            '.MuiDataGrid-row > div:nth-child(2)': {
              paddingLeft: '18px',
            },
            '.MuiDataGrid-rowCount': {
              color: C('Label/Light/Secondary'),
              backgroundColor: bg ?? C('Background / Light / System'),
              fontSize: scale(12),
            },
            '.MuiDataGrid-filler': {
              backgroundColor: bg ?? C('Background / Light / System'),
            },
            '.MuiDataGrid-menuIconButton': {
              color: C('Label/Light/Secondary'),
            },
            '.MuiDataGrid-sortIcon': {
              color: C('Label/Light/Secondary'),
            },
            '.MuiDataGrid-overlay': {
              color: C('Label/Light/Secondary'),
              backgroundColor: bg ?? C('Background / Light / System'),
              fontSize: scale(14),
            },
            '.MuiDataGrid-container--top': {
              '--DataGrid-rowBorderColor': C('Hairline/Light/No trans'),
            },
            '.MuiDataGrid-footerContainer': {
              display: 'none',
            },
            '.MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '.Mui-selected': {
              backgroundColor: 'transparent !important',
              border: `1px solid ${C('System/Dark/Blue')}`,
              borderRadius: '10px',
            },
            '.MuiDataGrid-booleanCell': {
              color: `${C('Label/Light/Secondary')} !important`,
              fontSize: scale(14),
            },
            '.MuiDataGrid-cell--pinnedLeft': {
              backgroundColor: bg ?? C('Background / Light / System'),
              '--DataGrid-rowBorderColor': C('Hairline/Light/No trans'),
            },
            '.MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft': {
              backgroundColor: C(modalFieldBg) + ' !important',
            },
          }}
        />
      </ThemeProvider>
    </View>
  );
};

export default ColorWrapper(DataGrid);
